// Owned by task 2.3 of the dashboard-redesign spec (Requirement 7).
//
// Reads the reserve-fund position — a **balance** endpoint plus a **12-month
// movement series** endpoint — and exposes them merged as
// `WidgetResult<ReserveFundPosition>` for `Widget_ReserveFundPosition` and
// `Widget_ReserveFundMovement` to consume. The reserve-funds sub-ledger
// reconciles to the GL Reserve control account per Constitution Rule 2;
// this hook is READ-ONLY, no mutation, no re-aggregation.
//
// Fetch contract (see design.md §Low-Level Design §Fetch contract):
//   - Starts in `{ status: "loading" }`.
//   - On mount and every `scope` change: ONE `AbortController` shared across
//     TWO parallel `api.get` calls issued via `Promise.all`. Both fetches
//     therefore abort together on unmount.
//   - Both succeed → merged into `ReserveFundPosition` with the fixed copy
//     `ringFencedNote = "Reserve funds are ring-fenced per building"`
//     (Requirement 7.2 / P5).
//   - Empty state when `balancePence === 0` AND `movements12M.length === 0`.
//   - Either fails → error state. The `errorId` is taken from whichever
//     error carries an `X-Error-Id` header; if neither does we generate a
//     UUID v4 locally. Partial-failure semantics are out of scope — reserve
//     fund is a single widget from the dashboard's perspective, so one
//     scoped error card covers both fetches.
//   - `retry` re-invokes both fetches.
//   - On unmount: aborts both in-flight fetches.
//
// The `api` client (frontend/src/services/api.ts) already attaches the Clerk
// JWT and the `x-clerk-subdomain` tenant header via interceptors — this hook
// MUST NOT re-implement any of that.
//
// Coordination note: tasks 2.1 (`useDashboardArrears`) and 2.2
// (`useCollectionRate`) share the same fetch pattern. The small helpers
// below are duplicated inline on purpose; a later refactor task will
// consolidate them into a `useWidgetFetch` primitive after all Wave-1 hooks
// have landed.

import { useCallback, useEffect, useState } from "react";
import type { AxiosError } from "axios";
import api from "../../../services/api";
import type {
  BuildingScope,
  WidgetResult,
  ReserveFundPosition,
} from "../types";

/** User-safe message shown in the scoped error card. Never leaks server text. */
const USER_SAFE_ERROR_MESSAGE = "Could not load reserve fund";

/** Fixed copy required by Requirement 7.2 (design.md §ReserveFundPosition). */
const RING_FENCED_NOTE = "Reserve funds are ring-fenced per building";

/**
 * Generate a UUID v4. Prefers `globalThis.crypto.randomUUID` when available;
 * falls back to a small `Math.random`-based hex formatter for environments
 * that don't expose the WebCrypto API (older test runners, JSDOM shims, etc.).
 * Never used to authenticate anything — only to correlate a UI error card to
 * a log entry when the server didn't return an `X-Error-Id` header.
 */
function generateLocalErrorId(): string {
  const cryptoRef = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return cryptoRef.randomUUID();
  }
  // RFC 4122 v4 shape: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx where y ∈ {8,9,a,b}.
  const rand = (): number => Math.floor(Math.random() * 16);
  const hex = (n: number): string => n.toString(16);
  let out = "";
  for (let i = 0; i < 36; i += 1) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      out += "-";
    } else if (i === 14) {
      out += "4";
    } else if (i === 19) {
      out += hex((rand() & 0x3) | 0x8);
    } else {
      out += hex(rand());
    }
  }
  return out;
}

/**
 * Extract an `X-Error-Id` header from an axios error's response. Case-
 * insensitive per RFC 7230; different transports normalise headers
 * differently (Node http lowercases everything, JSDOM leaves them as-is).
 */
function extractErrorIdFromAxios(err: unknown): string | undefined {
  if (!err || typeof err !== "object") return undefined;
  const response = (err as AxiosError).response;
  if (!response || !response.headers) return undefined;
  const headers = response.headers as
    | Record<string, string | undefined>
    | { get?: (key: string) => string | null };
  // Some axios versions expose headers as an AxiosHeaders instance with a
  // `get(key)` method; others expose a plain record. Handle both defensively.
  if (typeof (headers as { get?: unknown }).get === "function") {
    const raw = (headers as { get: (key: string) => string | null }).get(
      "x-error-id",
    );
    if (typeof raw === "string" && raw.length > 0) return raw;
  }
  const record = headers as Record<string, string | undefined>;
  const candidate =
    record["x-error-id"] ??
    record["X-Error-Id"] ??
    record["X-ERROR-ID"];
  return typeof candidate === "string" && candidate.length > 0
    ? candidate
    : undefined;
}

/** Parsed balance response, before merging with the movements response. */
interface BalanceResponse {
  balancePence: number;
  aggregatedFromBuildings: string[];
}

/**
 * Validate that an unknown response body has the shape of a balance response.
 * Returns the typed value on success or `undefined` on any structural
 * mismatch — the caller then transitions to the error state per the fetch
 * contract, so we never trust an unexpected payload.
 */
function parseBalance(body: unknown): BalanceResponse | undefined {
  if (!body || typeof body !== "object") return undefined;
  const b = body as Record<string, unknown>;
  const { balancePence, aggregatedFromBuildings } = b;
  if (
    typeof balancePence !== "number" ||
    !Number.isFinite(balancePence) ||
    !Number.isInteger(balancePence)
  ) {
    return undefined;
  }
  if (!Array.isArray(aggregatedFromBuildings)) return undefined;
  const cleaned: string[] = [];
  for (const id of aggregatedFromBuildings) {
    if (typeof id !== "string") return undefined;
    cleaned.push(id);
  }
  return { balancePence, aggregatedFromBuildings: cleaned };
}

/**
 * Validate that an unknown response body carries a `movements` array. The
 * inner shape is owned by the `reserve-funds` spec — we deliberately keep it
 * as `unknown[]` here so this hook doesn't couple to that spec's internal
 * model. Returns the raw array on success or `undefined` on mismatch.
 */
function parseMovements(body: unknown): unknown[] | undefined {
  if (!body || typeof body !== "object") return undefined;
  const b = body as Record<string, unknown>;
  if (!Array.isArray(b.movements)) return undefined;
  // Keep entries as-is; each element's inner shape is validated (or not) by
  // the widget layer that renders it.
  return b.movements.slice();
}

export function useReserveFundPosition(
  scope: BuildingScope,
): WidgetResult<ReserveFundPosition> {
  const [retryCounter, setRetryCounter] = useState(0);
  const [result, setResult] = useState<WidgetResult<ReserveFundPosition>>({
    status: "loading",
  });

  const retry = useCallback(() => {
    setRetryCounter((n) => n + 1);
  }, []);

  const scopeKind = scope.kind;
  const scopeBuildingId = scope.kind === "single" ? scope.buildingId : null;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    // Reset to loading on scope/retry change so consumers see the skeleton
    // between fetches instead of stale ready data.
    setResult({ status: "loading" });

    const buildingId =
      scopeKind === "all" ? "ALL" : (scopeBuildingId ?? "ALL");
    const encoded = encodeURIComponent(buildingId);
    const balanceUrl = `/reserve-funds/balance?buildingId=${encoded}`;
    const movementsUrl = `/reserve-funds/movements?range=12M&buildingId=${encoded}`;

    // ONE AbortController, shared across both requests. Passing the same
    // signal to each `api.get` means a single `controller.abort()` (on
    // unmount or on scope change) tears both fetches down together.
    Promise.all([
      api.get(balanceUrl, { signal: controller.signal }),
      api.get(movementsUrl, { signal: controller.signal }),
    ])
      .then(([balanceRes, movementsRes]) => {
        if (cancelled) return;
        const parsedBalance = parseBalance(balanceRes?.data);
        const parsedMovements = parseMovements(movementsRes?.data);
        if (!parsedBalance || !parsedMovements) {
          setResult({
            status: "error",
            errorId: generateLocalErrorId(),
            message: "Unexpected response shape",
            retry,
          });
          return;
        }

        const merged: ReserveFundPosition = {
          balancePence: parsedBalance.balancePence,
          aggregatedFromBuildings: parsedBalance.aggregatedFromBuildings,
          ringFencedNote: RING_FENCED_NOTE,
          movements12M: parsedMovements,
        };

        if (merged.balancePence === 0 && merged.movements12M.length === 0) {
          setResult({ status: "empty" });
          return;
        }

        setResult({ status: "ready", data: merged });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // The browser/axios raises `CanceledError` (or a `DOMException`
        // named `AbortError`) when the AbortController fires. That path is
        // the caller's intent, not a failure — swallow silently.
        if (controller.signal.aborted) return;
        // `Promise.all` short-circuits on the first rejection, so `err` is
        // whichever fetch failed first. That's the error we surface the
        // `X-Error-Id` header from; falling back to a client-generated UUID
        // when the server didn't set one.
        const errorId =
          extractErrorIdFromAxios(err) ?? generateLocalErrorId();
        setResult({
          status: "error",
          errorId,
          message: USER_SAFE_ERROR_MESSAGE,
          retry,
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `scope.kind` and `scope.buildingId` are the only inputs; `retryCounter`
    // triggers a re-fetch on `retry()`. `retry` and `api` are stable refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKind, scopeBuildingId, retryCounter]);

  return result;
}

// Exposed for tests that want to assert against the fixed copy without
// hard-coding the string in every assertion. Not for import by widgets —
// widgets should render `data.ringFencedNote` directly.
export const RESERVE_FUND_RING_FENCED_NOTE = RING_FENCED_NOTE;
